import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  message,
  Spin,
  Row,
  Col,
  Typography,
  Switch,
  Select,
  Space,
  Tag,
  Descriptions,
  Alert
} from 'antd';
import {
  BankOutlined,
  SettingOutlined,
  HomeOutlined,
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  EditOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { systemService, SystemConfig, SystemSummary } from '../../services/systemService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

interface SystemInfoPageProps {}

const SystemInfoPage: React.FC<SystemInfoPageProps> = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<SystemConfig[]>([]);
  const [summary, setSummary] = useState<SystemSummary | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  const [summaryForm] = Form.useForm();

  // Load dữ liệu
  const loadData = async () => {
    setLoading(true);
    try {
      const [configsData, summaryData] = await Promise.all([
        systemService.getAllConfigs(),
        systemService.getSystemSummary()
      ]);
      setConfigs(configsData);
      setSummary(summaryData);
      
      // Cập nhật form với dữ liệu hiện tại
      const formData: any = {};
      configsData.forEach(config => {
        formData[config.config_key] = config.config_value;
      });
      form.setFieldsValue(formData);
      summaryForm.setFieldsValue(summaryData);
    } catch (error) {
      message.error('Không thể tải thông tin hệ thống');
      console.error('Load system info error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Lưu cấu hình
  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const configsToUpdate: any[] = [];
      
      Object.keys(values).forEach(key => {
        const config = configs.find(c => c.config_key === key);
        if (config && config.config_value !== values[key]) {
          configsToUpdate.push({
            config_key: key,
            config_value: values[key],
            description: config.description
          });
        }
      });

      if (configsToUpdate.length > 0) {
        await systemService.updateMultipleConfigs(configsToUpdate);
        message.success('Cập nhật thông tin hệ thống thành công');
        loadData();
        setEditMode(false);
      } else {
        message.info('Không có thay đổi nào để lưu');
      }
    } catch (error) {
      message.error('Không thể cập nhật thông tin hệ thống');
      console.error('Save system info error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Lưu thông tin tổng hợp
  const handleSaveSummary = async (values: any) => {
    setSaving(true);
    try {
      const configsToUpdate: any[] = [];
      
      // Xử lý thông tin công ty
      Object.keys(values.company || {}).forEach(key => {
        configsToUpdate.push({
          config_key: `company_${key}`,
          config_value: values.company[key],
          description: `Thông tin ${key} của công ty`
        });
      });

      // Xử lý thông tin hệ thống
      Object.keys(values.system || {}).forEach(key => {
        configsToUpdate.push({
          config_key: `system_${key}`,
          config_value: values.system[key],
          description: `Thông tin ${key} của hệ thống`
        });
      });

      // Xử lý thông tin kho
      Object.keys(values.warehouse || {}).forEach(key => {
        configsToUpdate.push({
          config_key: `warehouse_${key}`,
          config_value: values.warehouse[key],
          description: `Thông tin ${key} của kho`
        });
      });

      if (configsToUpdate.length > 0) {
        await systemService.updateMultipleConfigs(configsToUpdate);
        message.success('Cập nhật thông tin tổng hợp thành công');
        loadData();
        setEditMode(false);
      }
    } catch (error) {
      message.error('Không thể cập nhật thông tin tổng hợp');
      console.error('Save summary error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Nhóm cấu hình theo loại
  const groupedConfigs = configs.reduce((acc, config) => {
    const prefix = config.config_key.split('_')[0];
    if (!acc[prefix]) acc[prefix] = [];
    acc[prefix].push(config);
    return acc;
  }, {} as Record<string, SystemConfig[]>);

  const renderConfigForm = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      disabled={!editMode}
    >
      <Row gutter={[16, 16]}>
        {Object.entries(groupedConfigs).map(([group, groupConfigs]) => (
          <Col xs={24} lg={12} key={group}>
            <Card
              title={
                <Space>
                  {group === 'company' && <BankOutlined />}
                  {group === 'system' && <SettingOutlined />}
                  {group === 'warehouse' && <HomeOutlined />}
                  {group === 'ui' && <SettingOutlined />}
                  {group === 'print' && <SettingOutlined />}
                  {group === 'security' && <SettingOutlined />}
                  {group === 'notification' && <SettingOutlined />}
                  {group === 'backup' && <SettingOutlined />}
                  {group === 'report' && <SettingOutlined />}
                  {group === 'min' && <SettingOutlined />}
                  {group === 'expiry' && <SettingOutlined />}
                  {group === 'telegram' && <SettingOutlined />}
                  {group === 'auto' && <SettingOutlined />}
                  {group === 'max' && <SettingOutlined />}
                  {group === 'current' && <SettingOutlined />}
                  {group === 'session' && <SettingOutlined />}
                  {group === 'password' && <SettingOutlined />}
                  {group === 'require' && <SettingOutlined />}
                  {group === 'email' && <SettingOutlined />}
                  {group === 'sms' && <SettingOutlined />}
                  {group === 'frequency' && <SettingOutlined />}
                  {group === 'retention' && <SettingOutlined />}
                  {group === 'currency' && <SettingOutlined />}
                  {group === 'date' && <SettingOutlined />}
                  {group === 'time' && <SettingOutlined />}
                  <Text strong style={{ textTransform: 'capitalize' }}>
                    {group === 'company' ? 'Công ty' :
                     group === 'system' ? 'Hệ thống' :
                     group === 'warehouse' ? 'Kho' :
                     group === 'ui' ? 'Giao diện' :
                     group === 'print' ? 'In ấn' :
                     group === 'security' ? 'Bảo mật' :
                     group === 'notification' ? 'Thông báo' :
                     group === 'backup' ? 'Sao lưu' :
                     group === 'report' ? 'Báo cáo' : group}
                  </Text>
                </Space>
              }
              size="small"
            >
              {groupConfigs.map(config => (
                <Form.Item
                  key={config.config_key}
                  label={
                    <Space>
                      <Text>{config.description || config.config_key}</Text>
                      {config.config_key.includes('_') && (
                        <Tag color="blue">
                          {config.config_key.split('_').slice(1).join('_')}
                        </Tag>
                      )}
                    </Space>
                  }
                  name={config.config_key}
                  rules={[
                    {
                      required: config.config_key.includes('name') || 
                               config.config_key.includes('phone') || 
                               config.config_key.includes('email'),
                      message: 'Trường này là bắt buộc'
                    }
                  ]}
                >
                  {config.config_key.includes('description') || 
                   config.config_key.includes('address') ? (
                    <TextArea rows={2} />
                  ) : config.config_key.includes('theme') || 
                       config.config_key.includes('language') || 
                       config.config_key.includes('timezone') ? (
                    <Select>
                      {config.config_key.includes('theme') && (
                        <>
                          <Option value="light">Sáng</Option>
                          <Option value="dark">Tối</Option>
                        </>
                      )}
                      {config.config_key.includes('language') && (
                        <>
                          <Option value="vi">Tiếng Việt</Option>
                          <Option value="en">English</Option>
                        </>
                      )}
                      {config.config_key.includes('timezone') && (
                        <>
                          <Option value="Asia/Ho_Chi_Minh">Việt Nam (GMT+7)</Option>
                          <Option value="UTC">UTC</Option>
                        </>
                      )}
                    </Select>
                  ) : config.config_key.includes('true') || 
                       config.config_key.includes('false') ? (
                    <Switch />
                  ) : (
                    <Input />
                  )}
                </Form.Item>
              ))}
            </Card>
          </Col>
        ))}
      </Row>
    </Form>
  );

  const renderSummaryView = () => (
    <div>
      {summary && (
        <>
          <Card title="Thông tin công ty" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Tên công ty" span={2}>
                {summary.company.name || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Địa chỉ" span={2}>
                {summary.company.address || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Số điện thoại">
                {summary.company.phone || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {summary.company.email || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {summary.company.website || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Mã số thuế">
                {summary.company.tax_code || 'Chưa cập nhật'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Thông tin hệ thống" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Tên hệ thống" span={2}>
                {summary.system.name || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Phiên bản">
                {summary.system.version || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn vị phát triển">
                {summary.system.developer || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Email hỗ trợ" span={2}>
                {summary.system.contact || 'Chưa cập nhật'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Thông tin kho">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Tên kho" span={2}>
                {summary.warehouse.name || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Địa chỉ kho" span={2}>
                {summary.warehouse.address || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Người quản lý">
                {summary.warehouse.manager || 'Chưa cập nhật'}
              </Descriptions.Item>
              <Descriptions.Item label="Số điện thoại kho">
                {summary.warehouse.phone || 'Chưa cập nhật'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}
    </div>
  );

  const renderSummaryForm = () => (
    <Form
      form={summaryForm}
      layout="vertical"
      onFinish={handleSaveSummary}
      disabled={!editMode}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title="Thông tin công ty" size="small">
            <Form.Item label="Tên công ty" name={['company', 'name']}>
              <Input />
            </Form.Item>
            <Form.Item label="Địa chỉ" name={['company', 'address']}>
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item label="Số điện thoại" name={['company', 'phone']}>
              <Input />
            </Form.Item>
            <Form.Item label="Email" name={['company', 'email']}>
              <Input />
            </Form.Item>
            <Form.Item label="Website" name={['company', 'website']}>
              <Input />
            </Form.Item>
            <Form.Item label="Mã số thuế" name={['company', 'tax_code']}>
              <Input />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Thông tin hệ thống" size="small">
            <Form.Item label="Tên hệ thống" name={['system', 'name']}>
              <Input />
            </Form.Item>
            <Form.Item label="Phiên bản" name={['system', 'version']}>
              <Input />
            </Form.Item>
            <Form.Item label="Đơn vị phát triển" name={['system', 'developer']}>
              <Input />
            </Form.Item>
            <Form.Item label="Email hỗ trợ" name={['system', 'contact']}>
              <Input />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Thông tin kho" size="small">
            <Form.Item label="Tên kho" name={['warehouse', 'name']}>
              <Input />
            </Form.Item>
            <Form.Item label="Địa chỉ kho" name={['warehouse', 'address']}>
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item label="Người quản lý" name={['warehouse', 'manager']}>
              <Input />
            </Form.Item>
            <Form.Item label="Số điện thoại kho" name={['warehouse', 'phone']}>
              <Input />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <SettingOutlined /> Thông tin hệ thống
        </Title>
        <Text type="secondary">
          Quản lý thông tin công ty, hệ thống và các cấu hình chung
        </Text>
      </div>

      <Alert
        message="Lưu ý"
        description="Các thay đổi trong thông tin hệ thống sẽ ảnh hưởng đến toàn bộ ứng dụng. Vui lòng cẩn thận khi chỉnh sửa."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card>
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button
              type={editMode ? 'default' : 'primary'}
              icon={editMode ? <EyeOutlined /> : <EditOutlined />}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? 'Xem' : 'Chỉnh sửa'}
            </Button>
            {editMode && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => {
                  form.submit();
                  summaryForm.submit();
                }}
                loading={saving}
              >
                Lưu thay đổi
              </Button>
            )}
          </Space>
        </div>

        <Spin spinning={loading}>
          <Tabs defaultActiveKey="summary">
            <TabPane
              tab={
                <span>
                  <InfoCircleOutlined />
                  Thông tin tổng hợp
                </span>
              }
              key="summary"
            >
              {editMode ? renderSummaryForm() : renderSummaryView()}
            </TabPane>
            
            <TabPane
              tab={
                <span>
                  <SettingOutlined />
                  Cấu hình chi tiết
                </span>
              }
              key="configs"
            >
              {renderConfigForm()}
            </TabPane>
          </Tabs>
        </Spin>
      </Card>
    </div>
  );
};

export default SystemInfoPage; 